import { useState, useEffect, ChangeEvent } from 'react'
import { motion } from 'framer-motion'
import {
  User,
  Mail,
  Phone,
  Building,
  MapPin,
  Calendar,
  Edit,
  Save,
  X,
  Crown,
  Star,
  Settings,
  Shield,
  CreditCard,
  Check,
  Loader,
  AlertTriangle,
  Camera,
  Briefcase,
  Gift
} from 'lucide-react'
import { apiClient, handleApiError } from '@/lib/api'
import CandidateHeader from '@/components/common/CandidateHeader'
import RecruiterHeaderNew from '@/components/common/RecruiterHeaderNew'
import ProfilePictureUpload from '@/components/common/ProfilePictureUpload'
import CreditBalanceWidget from '@/components/credits/CreditBalanceWidget'
import CouponRedemptionModal from '@/components/credits/CouponRedemptionModal'

interface IdentityDocumentInfo {
  uuid?: string
  status?: string
  s3Key?: string
  uploadedAt?: string
}

interface UserProfile {
  id: string
  firstName: string
  lastName: string
  email: string
  phone?: string
  company: string
  department?: string
  role: string
  location?: string
  bio?: string
  avatar?: string
  joinedDate: string
  lastLogin?: string
  isActive: boolean
  isRecruiter: boolean
  isJobseeker: boolean
  identityDocument?: IdentityDocumentInfo
  recruiterStatus?: string
  recruiterCompany?: string
  recruiterUseCase?: string
  recruiterIdCountry?: string
  recruiterIdType?: string
  recruiterIdDocumentKey?: string
  recruiterIdMimeType?: string
  recruiterIdUploadedAt?: string
}

interface SubscriptionPlan {
  id: string
  name: string
  type: 'free' | 'basic' | 'pro' | 'enterprise'
  price: number
  currency: string
  interval: 'monthly' | 'yearly'
  status: 'active' | 'canceled' | 'expired' | 'trial'
  startDate: string
  endDate: string
  features: string[]
  usage: {
    candidatesUsed: number
    candidatesLimit: number
    positionsUsed: number
    positionsLimit: number
    interviewsUsed: number
    interviewsLimit: number
  }
}

interface Credits {
  interview: number
  total: number
  breakdown: {
    [key: string]: number
  }
}

const normalizeCredits = (data: any): Credits => ({
  interview: Number(data?.interview ?? 0),
  total: Number(data?.total ?? 0),
  breakdown: typeof data?.breakdown === 'object' && data.breakdown !== null ? data.breakdown : {}
})

export default function ProfilePage() {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [subscription, setSubscription] = useState<SubscriptionPlan | null>(null)
  const [credits, setCredits] = useState<Credits | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const [showCouponModal, setShowCouponModal] = useState(false)
  const [creditWidgetKey, setCreditWidgetKey] = useState(0)
  const [identityUploading, setIdentityUploading] = useState(false)
  const [identityUploadProgress, setIdentityUploadProgress] = useState(0)
  const [identityUploadError, setIdentityUploadError] = useState<string | null>(null)
  const [mainTab, setMainTab] = useState<'profile' | 'credits' | 'recruiter'>('profile')

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token')
    setIsLoggedIn(!!token)
    
    loadProfileData()
  }, [])

  const loadProfileData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch profile data and credits in parallel
      const [profileResponse, creditsResponse] = await Promise.all([
        apiClient.request('/users/profile/me', { method: 'GET' }),
        apiClient.request('/users/credits', { method: 'GET' })
      ])
      
      if (!profileResponse.success || !profileResponse.data) {
        throw new Error(profileResponse.error || 'Failed to load profile data')
      }

      const userData = profileResponse.data
      const identityDocument: IdentityDocumentInfo | undefined = userData.identity_document ? {
        uuid: userData.identity_document.uuid,
        status: userData.identity_document.status,
        s3Key: userData.identity_document.s3_key || userData.identity_document.s3Key,
        uploadedAt: userData.identity_document.uploaded_at || userData.identity_document.uploadedAt
      } : undefined
      const profileData = {
        id: userData.id?.toString() || '',
        firstName: userData.first_name || userData.firstName || '',
        lastName: userData.last_name || userData.lastName || '', 
        email: userData.email || '',
        phone: userData.phone || '',
        company: userData.company_name || userData.company || 'HireHubble',
        department: userData.department || 'Department',
        role: userData.role || 'Role',
        location: userData.location || '',
        bio: userData.bio || '',
        avatar: userData.avatar_url || '',
        joinedDate: userData.created_at || userData.createdAt || '',
        lastLogin: userData.lastLogin || '',
        isActive: userData.is_active !== false,
        isRecruiter: userData.is_recruiter || false,
        isJobseeker: userData.is_jobseeker || false,
        identityDocument,
        recruiterStatus: userData.recruiter_application_status || userData.recruiter_status || userData.recruiterStatus,
        recruiterCompany: userData.recruiter_company || userData.recruiterCompany,
        recruiterUseCase: userData.recruiter_use_case || userData.recruiterUseCase,
        recruiterIdCountry: userData.recruiter_id_country || userData.recruiterIdCountry,
        recruiterIdType: userData.recruiter_id_type || userData.recruiterIdType,
        recruiterIdDocumentKey: userData.recruiter_id_s3_key || userData.recruiterIdDocumentKey || identityDocument?.s3Key,
        recruiterIdMimeType: userData.recruiter_id_mime_type || userData.recruiterIdMimeType,
        recruiterIdUploadedAt: userData.recruiter_id_uploaded_at || userData.recruiterIdUploadedAt
      }
      
      setProfile(profileData)
      
      // Update localStorage with current user data for header
      if (typeof window !== 'undefined') {
        localStorage.setItem('currentUser', JSON.stringify(profileData))
        // Dispatch custom event to notify header
        window.dispatchEvent(new Event('userDataUpdated'))
      }

      // Handle credits data
      if (creditsResponse.success && creditsResponse.data) {
        setCredits(normalizeCredits(creditsResponse.data))
      } else {
        console.warn('Failed to load credits:', creditsResponse.error)
        setCredits({ interview: 0, total: 0, breakdown: {} })
      }

      // Handle subscription data if available
      if (userData.subscription || userData.credits) {
        const creditsData = normalizeCredits(creditsResponse.success ? creditsResponse.data : userData.credits)
        setSubscription({
          id: '1',
          name: userData.subscription?.plan || 'Credits Plan',
          type: 'free',
          price: 0,
          currency: 'USD',
          interval: 'monthly',
          status: userData.subscription?.status || 'active',
          startDate: userData.created_at || userData.createdAt || '',
          endDate: '',
          features: ['Interview Credits System', 'Candidate Management', 'Position Posting'],
          usage: {
            candidatesUsed: 0,
            candidatesLimit: 100,
            positionsUsed: 0,
            positionsLimit: 50,
            interviewsUsed: 0,
            interviewsLimit: creditsData?.interview || creditsData?.total || 0
          }
        })
      } else {
        // Set default subscription with credits
        const creditsData = normalizeCredits(creditsResponse.success ? creditsResponse.data : { interview: 0, total: 0 })
        setSubscription({
          id: '1',
          name: 'Credits Plan',
          type: 'free',
          price: 0,
          currency: 'USD',
          interval: 'monthly',
          status: 'active',
          startDate: userData.created_at || userData.createdAt || new Date().toISOString(),
          endDate: '',
          features: ['Interview Credits System', 'Candidate Management', 'Position Posting'],
          usage: {
            candidatesUsed: 0,
            candidatesLimit: 100,
            positionsUsed: 0,
            positionsLimit: 50,
            interviewsUsed: 0,
            interviewsLimit: creditsData.interview || creditsData.total || 0
          }
        })
      }

    } catch (err) {
      console.error('Failed to load profile data:', err)
      setError(handleApiError(err))
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: keyof UserProfile, value: any) => {
    setProfile(prev => prev ? { ...prev, [field]: value } : null)
  }

  const handleSave = async () => {
    if (!profile) return

    setSaving(true)
    try {
      const response = await apiClient.request('/users/profile/me', {
        method: 'PUT',
        body: JSON.stringify({
          first_name: profile.firstName,
          last_name: profile.lastName,
          phone: profile.phone,
          location: profile.location,
          bio: profile.bio,
          title: profile.role,
          department_id: null // Would need department ID mapping
        })
      })
      
      if (response.success) {
        setIsEditing(false)
        // Update localStorage and notify header
        if (typeof window !== 'undefined') {
          localStorage.setItem('currentUser', JSON.stringify(profile))
          window.dispatchEvent(new Event('userDataUpdated'))
        }
        alert('Profile updated successfully!')
      } else {
        alert(`Failed to update profile: ${response.error}`)
      }
    } catch (err) {
      alert('Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handleAvatarUploadSuccess = (newAvatarUrl: string) => {
    // Update the profile state with new avatar URL
    setProfile(prev => prev ? { ...prev, avatar: newAvatarUrl } : null)
    
    // Update localStorage and notify header
    if (typeof window !== 'undefined' && profile) {
      const updatedProfile = { ...profile, avatar: newAvatarUrl }
      localStorage.setItem('currentUser', JSON.stringify(updatedProfile))
      window.dispatchEvent(new Event('userDataUpdated'))
    }
    
    // Show success message
    alert('Profile picture updated successfully!')
  }

  const handleAvatarUploadError = (error: string) => {
    alert(`Failed to upload profile picture: ${error}`)
  }

  const handleIdentityUpload = async (file: File) => {
    if (!file) return

    setIdentityUploadError(null)
    setIdentityUploading(true)
    setIdentityUploadProgress(0)

    try {
      const presigned = await apiClient.request('/file-uploads/identity/presigned-url', {
        method: 'POST',
        body: JSON.stringify({
          original_filename: file.name,
          file_size: file.size,
          mime_type: file.type
        })
      })

      if (!presigned.success || !presigned.data) {
        throw new Error(presigned.error || 'Failed to get upload URL')
      }

      const { upload_url, fields, s3_key } = presigned.data as any

      if (!upload_url || !fields || !s3_key) {
        throw new Error('Invalid upload configuration received')
      }

      setIdentityUploadProgress(25)

      const formData = new FormData()
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value as string)
      })
      formData.append('file', file)

      const uploadResponse = await fetch(upload_url, {
        method: 'POST',
        body: formData
      })

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text()
        throw new Error(`Storage upload failed: ${uploadResponse.status} ${errorText}`)
      }

      setIdentityUploadProgress(75)

      const confirmResponse = await apiClient.request('/file-uploads/identity/confirm-upload', {
        method: 'POST',
        body: JSON.stringify({
          s3_key,
          original_filename: file.name,
          file_size: file.size,
          mime_type: file.type,
          id_country: profile?.recruiterIdCountry,
          id_type: profile?.recruiterIdType,
          recruiter_company: profile?.recruiterCompany,
          recruiter_use_case: profile?.recruiterUseCase
        })
      })

      if (!confirmResponse.success || !confirmResponse.data) {
        throw new Error(confirmResponse.error || 'Failed to confirm identity upload')
      }

      const docData = confirmResponse.data as any
      setIdentityUploadProgress(100)

      setProfile(prev => {
        if (!prev) return prev
        const updatedProfile = {
          ...prev,
          recruiterIdDocumentKey: s3_key,
          recruiterIdMimeType: file.type,
          recruiterIdUploadedAt: new Date().toISOString(),
          identityDocument: {
            uuid: docData.identity_document_uuid || docData.uuid,
            status: docData.status || 'pending_review',
            s3Key: docData.s3_key || s3_key,
            uploadedAt: new Date().toISOString()
          }
        }

        if (typeof window !== 'undefined') {
          localStorage.setItem('currentUser', JSON.stringify(updatedProfile))
          window.dispatchEvent(new Event('userDataUpdated'))
        }

        return updatedProfile
      })

      alert('ID uploaded successfully. We will review it shortly.')

    } catch (err) {
      setIdentityUploadError(err instanceof Error ? err.message : 'Failed to upload identity document')
    } finally {
      setTimeout(() => setIdentityUploadProgress(0), 600)
      setIdentityUploading(false)
    }
  }

  const handleIdentityFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handleIdentityUpload(file)
      event.target.value = ''
    }
  }

  const saveRecruiterApplication = async () => {
    if (!profile) return
    await apiClient.request('/users/recruiter/application', {
      method: 'POST',
      body: JSON.stringify({
        recruiter_company: profile.recruiterCompany,
        recruiter_use_case: profile.recruiterUseCase,
        recruiter_id_country: profile.recruiterIdCountry,
        recruiter_id_type: profile.recruiterIdType
      })
    })
  }

  const handleEnableRecruiter = async () => {
    try {
      if (!profile?.recruiterCompany || !profile?.recruiterUseCase || !profile?.recruiterIdCountry || !profile?.recruiterIdType) {
        alert('Please provide your company, use case, ID country, and ID type before applying for recruiter access.')
        return
      }
      if (!canEnableRecruiter) {
        const message = profile?.identityDocument?.status === 'rejected'
          ? 'Your previous ID was rejected. Please upload a new document before enabling recruiter access.'
          : 'Please upload a government ID before enabling recruiter access.'
        alert(message)
        return
      }

      await saveRecruiterApplication()

      setSaving(true)
      const response = await apiClient.request('/users/enable-recruiter', { method: 'POST' })
      
      if (response.success) {
        setProfile(prev => prev ? { ...prev, isRecruiter: true } : null)
        // Update localStorage and notify header
        if (typeof window !== 'undefined' && profile) {
          const updatedProfile = { ...profile, isRecruiter: true }
          localStorage.setItem('currentUser', JSON.stringify(updatedProfile))
          window.dispatchEvent(new Event('userDataUpdated'))
        }
        alert('Recruiter mode enabled! You can now access the recruiter dashboard.')
      } else {
        alert(`Failed to enable recruiter mode: ${response.error}`)
      }
    } catch (err) {
      alert('Failed to enable recruiter mode')
    } finally {
      setSaving(false)
    }
  }

  const handleEnableJobseeker = async () => {
    try {
      setSaving(true)
      const response = await apiClient.request('/users/enable-jobseeker', { method: 'POST' })
      
      if (response.success) {
        setProfile(prev => prev ? { ...prev, isJobseeker: true } : null)
        // Update localStorage and notify header
        if (typeof window !== 'undefined' && profile) {
          const updatedProfile = { ...profile, isJobseeker: true }
          localStorage.setItem('currentUser', JSON.stringify(updatedProfile))
          window.dispatchEvent(new Event('userDataUpdated'))
        }
        alert('Jobseeker mode enabled! You can now apply for jobs.')
      } else {
        alert(`Failed to enable jobseeker mode: ${response.error}`)
      }
    } catch (err) {
      alert('Failed to enable jobseeker mode')
    } finally {
      setSaving(false)
    }
  }

  const handleCouponRedemptionSuccess = (creditsAdded: number) => {
    // Refresh credit data
    setCreditWidgetKey(prev => prev + 1)
    setShowCouponModal(false)
    
    // Show success message
    alert(`Successfully added ${creditsAdded} credits to your account!`)
  }

  const getPlanColor = (type: string) => {
    switch (type) {
      case 'free': return 'bg-gray-100 text-gray-800'
      case 'basic': return 'bg-blue-100 text-blue-800'
      case 'pro': return 'bg-purple-100 text-purple-800'
      case 'enterprise': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'trial': return 'bg-yellow-100 text-yellow-800'
      case 'canceled': case 'expired': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getIdentityStatusStyles = (status?: string) => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      case 'pending_review':
      default:
        return status ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'
    }
  }

  const formatIdentityStatus = (status?: string) => {
    if (!status) return 'Not submitted'
    return status
      .split('_')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
  }

  const calculateUsagePercentage = (used: number, limit: number) => {
    if (limit === 0) return 0
    return Math.min((used / limit) * 100, 100)
  }

  // Determine which header to show based on user role
  const getHeader = () => {
    if (!isLoggedIn || !profile) return null
    
    // If user is both recruiter and jobseeker, prioritize based on current context
    // For the /profile page, we'll show recruiter header if they have recruiter access
    if (profile.isRecruiter) {
      return <RecruiterHeaderNew />
    } else if (profile.isJobseeker) {
      return <CandidateHeader />
    }
    
    // Default to CandidateHeader if no specific role is set
    return <CandidateHeader />
  }

  const canEnableRecruiter = !!(profile?.identityDocument?.uuid && profile?.identityDocument?.status !== 'rejected')

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        {isLoggedIn && <CandidateHeader />}
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Loading profile...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        {isLoggedIn && <CandidateHeader />}
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Profile</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button onClick={loadProfileData} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700">
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {getHeader()}
      <div className={`max-w-4xl mx-auto space-y-6 ${isLoggedIn ? 'pt-8' : 'py-8'} px-4`}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
            <p className="text-gray-600 mt-1">Manage your personal information and subscription</p>
          </div>
          
          <div className="flex items-center space-x-3">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  disabled={saving}
                  className="btn-secondary"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-primary"
                >
                  {saving ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="btn-primary"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-center gap-2">
          {[
            { key: 'profile', label: 'Profile' },
            { key: 'credits', label: 'Credits & plan' },
            { key: 'recruiter', label: 'Recruiter access' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setMainTab(tab.key as any)}
              className={`px-3 py-2 rounded-md text-sm font-medium border ${
                mainTab === tab.key ? 'bg-indigo-50 border-indigo-200 text-indigo-700' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {mainTab === 'profile' && (
          <div className="space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-6 mb-6">
                <ProfilePictureUpload
                  currentAvatarUrl={profile?.avatar}
                  onUploadSuccess={handleAvatarUploadSuccess}
                  onUploadError={handleAvatarUploadError}
                  size="md"
                />
                
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {profile?.firstName} {profile?.lastName}
                  </h3>
                  <p className="text-gray-600">{profile?.role}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <Building className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{profile?.company}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profile?.firstName || ''}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-900">{profile?.firstName}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profile?.lastName || ''}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <div className="flex items-center">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-900">{profile?.lastName}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <div className="flex items-center">
                    <Mail className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-gray-900">{profile?.email}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={profile?.phone || ''}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <div className="flex items-center">
                      <Phone className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-900">{profile?.phone || 'Not provided'}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profile?.department || ''}
                      onChange={(e) => handleInputChange('department', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <div className="flex items-center">
                      <Briefcase className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-900">{profile?.department || 'Department'}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profile?.location || ''}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-900">{profile?.location || 'Not provided'}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role / Title</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={profile?.role || ''}
                      onChange={(e) => handleInputChange('role', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <div className="flex items-center">
                      <Shield className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-900">{profile?.role || 'Role'}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                  {isEditing ? (
                    <textarea
                      value={profile?.bio || ''}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <p className="text-gray-900 whitespace-pre-wrap">{profile?.bio || 'No bio provided'}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {profile?.joinedDate && (
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">Joined:</span>
                    </div>
                    <span className="text-sm text-gray-900">{new Date(profile.joinedDate).toLocaleDateString()}</span>
                  </div>
                )}

                {profile?.lastLogin && (
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center">
                      <Settings className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600">Last Login:</span>
                    </div>
                    <span className="text-sm text-gray-900">{new Date(profile.lastLogin).toLocaleDateString()}</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center">
                    <Shield className="w-4 h-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">Account Status:</span>
                  </div>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${profile?.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {profile?.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {mainTab === 'credits' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Plan</h3>
              {subscription ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <Crown className="w-5 h-5 text-yellow-500 mr-2" />
                      <span className="font-medium text-gray-900">{subscription.name}</span>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPlanColor(subscription.type)}`}>
                      {subscription.type.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Status:</span>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(subscription.status)}`}>
                      {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Price:</span>
                    <span className="text-sm font-medium text-gray-900">{subscription.price === 0 ? 'Free' : `$${subscription.price}/${subscription.interval}`}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Valid Until:</span>
                    <span className="text-sm text-gray-900">{new Date(subscription.endDate).toLocaleDateString()}</span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <CreditCard className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-600">No subscription information available</p>
                </div>
              )}
            </div>

            <CreditBalanceWidget key={creditWidgetKey} showHistory={true} />

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Get More Credits</h3>
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                      <Gift className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Have a Coupon Code?</div>
                      <div className="text-sm text-gray-600">Redeem your coupon to add credits instantly</div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setShowCouponModal(true)}
                  className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center justify-center space-x-2"
                >
                  <Gift className="w-5 h-5" />
                  <span>Redeem Coupon</span>
                </button>
              </div>
            </div>

            {subscription && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Plan Features</h3>
                <div className="space-y-2">
                  {subscription.features.map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {subscription?.usage && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Usage This Month</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Candidates</span>
                      <span className="text-sm text-gray-600">{subscription.usage.candidatesUsed} / {subscription.usage.candidatesLimit}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${calculateUsagePercentage(subscription.usage.candidatesUsed, subscription.usage.candidatesLimit)}%` }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Positions</span>
                      <span className="text-sm text-gray-600">{subscription.usage.positionsUsed} / {subscription.usage.positionsLimit}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-green-600 h-2 rounded-full transition-all duration-300" style={{ width: `${calculateUsagePercentage(subscription.usage.positionsUsed, subscription.usage.positionsLimit)}%` }} />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">Interviews</span>
                      <span className="text-sm text-gray-600">{subscription.usage.interviewsUsed} / {subscription.usage.interviewsLimit}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-purple-600 h-2 rounded-full transition-all duration-300" style={{ width: `${calculateUsagePercentage(subscription.usage.interviewsUsed, subscription.usage.interviewsLimit)}%` }} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {subscription && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Plan Management</h3>
                <div className="space-y-3">
                  {subscription.type === 'free' ? (
                    <button className="w-full btn-primary">
                      <Star className="w-4 h-4 mr-2" />
                      Upgrade to Pro
                    </button>
                  ) : (
                    <>
                      <button className="w-full btn-secondary">
                        <CreditCard className="w-4 h-4 mr-2" />
                        Manage Billing
                      </button>
                      <button className="w-full btn-secondary">
                        <Settings className="w-4 h-4 mr-2" />
                        Change Plan
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {mainTab === 'recruiter' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Recruiter application</h3>
                  <p className="text-sm text-gray-600 mt-1">Upload a government ID to unlock recruiter tools.</p>
                </div>
                <span className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getIdentityStatusStyles(profile?.identityDocument?.status)}`}>
                  {formatIdentityStatus(profile?.identityDocument?.status)}
                </span>
              </div>

              <div className="text-sm text-gray-600 mt-2">
                Recruiter status: <span className="font-medium text-gray-900">{profile?.recruiterStatus || 'pending_review'}</span>
                <button
                  onClick={loadProfileData}
                  className="ml-2 text-xs text-indigo-600 hover:text-indigo-800 underline"
                >
                  Refresh
                </button>
              </div>

              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Company</label>
                    <input
                      type="text"
                      value={profile?.recruiterCompany || ''}
                      onChange={(e) => handleInputChange('recruiterCompany', e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Your company"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">How will you use HireHubble?</label>
                    <input
                      type="text"
                      value={profile?.recruiterUseCase || ''}
                      onChange={(e) => handleInputChange('recruiterUseCase', e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Sourcing, screening, interviewing, etc."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">ID country</label>
                    <input
                      type="text"
                      value={profile?.recruiterIdCountry || ''}
                      onChange={(e) => handleInputChange('recruiterIdCountry', e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., India"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">ID type</label>
                    <input
                      type="text"
                      value={profile?.recruiterIdType || ''}
                      onChange={(e) => handleInputChange('recruiterIdType', e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Passport, Driver License, etc."
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Identity document (PDF or image)</label>
                  <div className="flex items-center flex-wrap gap-3">
                    <label className={`px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 ${identityUploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        onChange={handleIdentityFileChange}
                        className="hidden"
                        disabled={identityUploading}
                      />
                      <span className="text-sm text-gray-700">{identityUploading ? 'Uploading...' : 'Upload ID'}</span>
                    </label>

                    {identityUploadProgress > 0 && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <Loader className="w-4 h-4 animate-spin" />
                        <span>Upload progress {identityUploadProgress}%</span>
                      </div>
                    )}

                    {profile?.identityDocument?.uploadedAt && (
                      <div className="text-xs text-gray-500">Last uploaded {new Date(profile.identityDocument?.uploadedAt || '').toLocaleString()}</div>
                    )}
                  </div>
                  {identityUploadError && <div className="text-sm text-red-600">{identityUploadError}</div>}
                  {profile?.recruiterIdDocumentKey && (
                    <div className="text-xs text-gray-600">
                      Current ID: {profile.recruiterIdDocumentKey} ({profile.recruiterIdMimeType || 'unknown type'})
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="mr-3">
                    <div className="text-sm font-medium text-gray-800">Enable recruiter access</div>
                    <div className="text-xs text-gray-600">Submit an ID, then activate your recruiter tools.</div>
                  </div>
                  {profile?.isRecruiter ? (
                    <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      <Check className="w-3 h-3 mr-1" /> Enabled
                    </span>
                  ) : (
                    <button
                      onClick={handleEnableRecruiter}
                      disabled={identityUploading || !canEnableRecruiter || saving}
                      className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saving ? 'Enabling...' : canEnableRecruiter ? 'Enable recruiter tools' : 'Upload ID first'}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Type</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Briefcase className={`w-5 h-5 ${profile?.isRecruiter ? 'text-blue-600' : 'text-gray-400'}`} />
                    <div>
                      <div className="font-medium text-gray-900">Recruiter</div>
                      <div className="text-sm text-gray-600">Post jobs and manage hiring (requires ID upload)</div>
                    </div>
                  </div>
                  {profile?.isRecruiter ? (
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      <Check className="w-3 h-3 mr-1" /> Enabled
                    </span>
                  ) : (
                    <button
                      onClick={handleEnableRecruiter}
                      disabled={saving || identityUploading || !canEnableRecruiter}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saving ? 'Enabling...' : canEnableRecruiter ? 'Enable' : 'Upload ID first'}
                    </button>
                  )}
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <User className={`w-5 h-5 ${profile?.isJobseeker ? 'text-green-600' : 'text-gray-400'}`} />
                    <div>
                      <div className="font-medium text-gray-900">Job Seeker</div>
                      <div className="text-sm text-gray-600">Search and apply for jobs</div>
                    </div>
                  </div>
                  {profile?.isJobseeker ? (
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      <Check className="w-3 h-3 mr-1" /> Enabled
                    </span>
                  ) : (
                    <button
                      onClick={handleEnableJobseeker}
                      disabled={saving}
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {saving ? 'Enabling...' : 'Enable'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Coupon Redemption Modal */}
      <CouponRedemptionModal
        isOpen={showCouponModal}
        onClose={() => setShowCouponModal(false)}
        onSuccess={handleCouponRedemptionSuccess}
      />
    </div>
  )
}
